import { components, type DropdownIndicatorProps, type GroupBase, type ValueContainerProps } from "react-select";
import { CheckIcon, ChevronDownIcon, XMarkIcon } from "@heroicons/react/24/outline";

export const MULTI_CHECK_CLASS = "multi-check-select";

/** Compare les options par id, sinon par valeur simple, pour que la case reste cochée après un rechargement. */
export function multiCheckGetOptionValue(option: { value?: any }): string {
  const value = option?.value;
  if (value && typeof value === "object") {
    if (value.id != null && value.id !== "") return String(value.id);
    if (value.label != null && value.label !== "") return `label:${value.label}`;
  }
  return value == null ? "" : String(value);
}

/**
 * Champ multi-choix : les valeurs choisies s'affichent en badges.
 * La sélection se lit dans le menu (case à gauche, coche à droite).
 */
const OptionSelect = (props: any) => {
  const checked = Boolean(props.isSelected);

  return (
    <components.Option
      {...props}
      className={`${props.className ?? ""} multi-check-option`.trim()}
    >
      <span aria-hidden className={`multi-check-box${checked ? " is-checked" : ""}`}>
        <CheckIcon strokeWidth={2.5} />
      </span>
      <span className="multi-check-label">{props.label}</span>
    </components.Option>
  );
};

const MultiCheckValueContainer = (
  props: ValueContainerProps<any, boolean, GroupBase<any>>
) => {
  const children = Array.isArray(props.children) ? props.children : [props.children];
  const input = children[children.length - 1];
  const isSearching = Boolean(props.selectProps.inputValue);
  const selected = props.getValue();

  const removeAt = (index: number) => (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    const current = props.getValue();
    const removed = current[index];
    // setValue n'accepte que select-option | deselect-option ; même chemin que react-select
    // quand on décoche une option multi.
    props.setValue(
      current.filter((_, itemIndex) => itemIndex !== index),
      "deselect-option",
      removed
    );
  };

  return (
    <components.ValueContainer {...props}>
      {selected.length > 0 && (
        <div className="multi-check-badges">
          {selected.map((option, index) => {
            const label = String(option?.label ?? "");
            return (
              <span key={`${label}-${index}`} className="multi-check-badge" title={label}>
                <span className="multi-check-badge-label">{label}</span>
                {!props.isDisabled && (
                  <button
                    type="button"
                    className="multi-check-badge-remove"
                    aria-label={`Retirer ${label}`}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                    }}
                    onClick={removeAt(index)}
                  >
                    <XMarkIcon strokeWidth={2.5} />
                  </button>
                )}
              </span>
            );
          })}
        </div>
      )}
      {!isSearching && selected.length === 0 && (
        <div className="multi-check-placeholder">{props.selectProps.placeholder}</div>
      )}
      {input}
    </components.ValueContainer>
  );
};

const MultiCheckDropdownIndicator = (
  props: DropdownIndicatorProps<any, boolean, GroupBase<any>>
) => (
  <components.DropdownIndicator {...props}>
    <ChevronDownIcon className="h-4 w-4" strokeWidth={2} />
  </components.DropdownIndicator>
);

export const multiCheckComponents = {
  Option: OptionSelect,
  ValueContainer: MultiCheckValueContainer,
  DropdownIndicator: MultiCheckDropdownIndicator,
  IndicatorSeparator: () => null,
  ClearIndicator: () => null,
  MultiValue: () => null,
};

export function multiCheckClassName(className = "") {
  return `${className} ${MULTI_CHECK_CLASS}`.trim();
}

export default OptionSelect;
